import { X } from "@untitledui/icons";
import { useEffect, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { cx } from "@/utils/cx";

const SQUARES = 28;
const LIT = 21;
const TICK_MS = 55;
const CYCLE_TICKS = 49;
const DURATION_MS = 9000;
const CLOSE_DELAY_MS = 700;

const useTicks = (isActive: boolean) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!isActive) return;
    const interval = setInterval(() => setTick((value) => (value + 1) % CYCLE_TICKS), TICK_MS);
    return () => clearInterval(interval);
  }, [isActive]);
  return tick;
};

const useProgress = (isActive: boolean, onDone: () => void) => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!isActive) return;
    const start = performance.now();
    let frame = 0;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const step = (now: number) => {
      const value = Math.min(1, (now - start) / DURATION_MS);
      setProgress(value);
      if (value < 1) frame = requestAnimationFrame(step);
      else timeout = setTimeout(onDone, CLOSE_DELAY_MS);
    };
    frame = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(frame);
      if (timeout) clearTimeout(timeout);
    };
  }, [isActive, onDone]);
  return progress;
};

const isLit = (index: number, tick: number) => {
  const head = tick - (CYCLE_TICKS - SQUARES - LIT);
  return index <= head && index > head - LIT;
};

export const MerchantAddedModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const tick = useTicks(isOpen);
  const progress = useProgress(isOpen, onClose);
  const dots = ".".repeat(Math.floor(tick / 11) % 4);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        hideCloseButton
        className="relative max-w-[940px] gap-0 overflow-hidden rounded-2xl border-0 px-12 py-11 text-white sm:rounded-2xl"
        style={{ background: "linear-gradient(145deg, #1f1029 0%, #351d4b 52%, #190b22 100%)" }}
      >
        <img
          src="/safety/aa254be04a7ba1180f1bbe583d7b9922e26a1a8a.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 h-full w-[45%] object-cover opacity-60"
          style={{ maskImage: "linear-gradient(to right, transparent, white 45%)" }}
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-5 right-5 z-10 text-white/60 hover:text-white"
        >
          <X className="size-5" />
        </button>

        <div className="relative z-10 flex max-w-[520px] flex-col gap-6">
          <DialogTitle className="text-[40px] font-medium leading-[46px] tracking-tight text-white">
            Shop configured.
            <br />
            <span className="text-brand-200">Analysing all videos.</span>
          </DialogTitle>
          <DialogDescription className="text-md text-brand-100">
            We are analysing your videos. This can take up to 48 hours. We'll let you know once this is complete.
          </DialogDescription>

          <div className="flex flex-col gap-3" aria-hidden="true">
            <div className="flex gap-1">
              {Array.from({ length: SQUARES }, (_, index) => (
                <span
                  key={index}
                  className={cx(
                    "size-3.5 rounded-[5px] transition-colors duration-300",
                    !isLit(index, tick) && "bg-white/15",
                  )}
                  style={
                    isLit(index, tick) ? { background: "linear-gradient(180deg, #DFB2AB 0%, #8059CF 100%)" } : undefined
                  }
                />
              ))}
            </div>
            <span className="text-sm text-brand-100">Analysing videos{dots}</span>
          </div>

          <Button color="secondary" size="md" className="self-start" onClick={onClose}>
            Got it
          </Button>
        </div>

        <div className="absolute inset-x-0 bottom-0 h-1 bg-white/10">
          <div
            className="h-full"
            style={{ width: `${progress * 100}%`, background: "linear-gradient(90deg, #DFB2AB 0%, #8059CF 100%)" }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
